// app/components/PendingWarningModal.tsx
import { Portal, Modal, Text, Button, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Employment } from '../lib/types';
import { useEffect, useState } from 'react';
import { Platform, ScrollView } from 'react-native';
import { AppStoreLinks } from './appStores/AppStoreLinks';
import { currentMobileWebPlatform } from '../utils/mobileWebPlatform';

interface PendingWarningModalProps {
  visible: boolean;
  effectiveProfile?: 'Owner' | 'Driver';
  employments: Employment[];
  onAddAnotherCompany?: () => void;
  onSwitchProfile?: (newProfile: 'Owner' | 'Driver') => void;
  onLogout?: () => void;
  paymentIssue?: boolean;
  onFixPayment?: () => void | Promise<void>;
  /**
   * #207: esperar pela empresa não pode ser a única saída. Quem não quer
   * esperar paga a sua própria licença e entra na hora (WS7) — o vínculo
   * declarado fica informativo, para o relatório do owner.
   */
  onSelfPay?: () => void;
}

export const PendingWarningModal = ({
  visible,
  effectiveProfile,
  employments,
  onAddAnotherCompany,
  onSwitchProfile,
  onLogout,
  onSelfPay,
  paymentIssue = false,
  onFixPayment,
}: PendingWarningModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showInstallLinks, setShowInstallLinks] = useState(false);
  const canInstall = effectiveProfile === 'Driver' && Platform.OS === 'web' && currentMobileWebPlatform() !== null;
  useEffect(() => {
    if (!visible || !canInstall) setShowInstallLinks(false);
  }, [visible, canInstall]);
  const latestEmployment = employments[0];
  const nipc = latestEmployment?.company_nipc || 'Unknown';
  const message = paymentIssue
    ? t(
        'paymentIssueMessage',
        'A sua licença está pendente por causa do pagamento. Corrija o método de pagamento para recuperar o acesso.',
      )
    : t('pendingEmploymentMessage', { nipc });

  return (
    <Portal>
      <Modal visible={visible} contentContainerStyle={[
        { backgroundColor: theme.colors.surface, padding: 20, margin: 20, borderRadius: 8, maxHeight: '90%' },
        Platform.OS === 'web' ? {
          maxWidth: 500,
          width: '90%',
          alignSelf: 'center',
        } : {}
      ]}>
        <ScrollView>
        <Text testID="pending-activation-title" variant="headlineSmall" style={{ marginBottom: 10 }}>{t('waitingForActivation')}</Text>
        <Text style={{ color: theme.colors.error, marginBottom: 20 }}>{message}</Text>
        {paymentIssue && onFixPayment ? (
          <Button
            testID="pending-payment-cta"
            mode="contained"
            icon="credit-card-refresh-outline"
            onPress={async () => {
              setPaymentError(null);
              try {
                await onFixPayment();
              } catch (error) {
                setPaymentError(error instanceof Error ? error.message : t('fixPaymentError', 'Não foi possível abrir a gestão de pagamento.'));
              }
            }}
            style={{ marginBottom: 10 }}
          >
            {t('fixPaymentCta', 'Corrigir pagamento')}
          </Button>
        ) : onSelfPay ? (
          <>
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
              {t('pendingSelfPayHint', 'Não quer esperar? Pode ativar já a sua própria licença.')}
            </Text>
            <Button
              testID="pending-selfpay-cta"
              mode="contained"
              icon="card-account-details-outline"
              onPress={onSelfPay}
              style={{ marginBottom: 10 }}
            >
              {t('pendingSelfPayCta', 'Eu pago a minha licença')}
            </Button>
          </>
        ) : null}
        {paymentError && (
          <Text accessibilityRole="alert" style={{ color: theme.colors.error, marginBottom: 10 }}>
            {paymentError}
          </Text>
        )}
        {onAddAnotherCompany && (
          <Button mode="contained" onPress={onAddAnotherCompany}>
            {t('addAnotherCompany')}
          </Button>
        )}
        {onSwitchProfile && (
          <Button mode="outlined" onPress={() => onSwitchProfile('Owner')} style={{ marginTop: 10 }}>
            {t('ownerButton')}
          </Button>
        )}
        {onLogout && (
          <Button mode="outlined" icon="logout" onPress={onLogout} style={{ marginTop: 10 }}>
            {t('logout')}
          </Button>
        )}
        {canInstall && <>
          <Button testID="pending-download-app" mode="outlined" icon="cellphone-arrow-down"
            onPress={() => setShowInstallLinks(value => !value)} style={{ marginTop: 10 }}>
            {t('register:downloadApp')}
          </Button>
          {showInstallLinks && <AppStoreLinks />}
        </>}
        </ScrollView>
      </Modal>
    </Portal>
  );
};